/**
 * App Module - Main Application Coordinator
 * 
 * This module coordinates all application modules and manages
 * the overall application lifecycle.
 * Now accepts dependencies through constructor for proper DI.
 * 
 * @module modules/App
 */

// Import modules
import AppState from '../core/AppState.js';
import EventBus, { AppEvents } from '../core/EventBus.js';
import Config from '../core/Config.js';
import container from '../core/ServiceContainer.js';
import ModuleManager from '../core/ModuleManager.js';
import store from '../store/index.js';
// Import feature modules
import { ChatModule } from './chat/ChatModule.js';
import { PortfolioModule } from './portfolio/PortfolioModule.js';
import { InitiativesModule } from './initiatives/InitiativesModule.js';
import { AIModule } from './ai/AIModule.js';
import { SettingsModule } from './settings/SettingsModule.js';
import { StrategyModule } from './strategy/StrategyModule.js';
import { InsightsModule } from './insights/InsightsModule.js';
import { BusinessLeversModule } from './levers/BusinessLeversModule.js';
import { CausalityModule } from './causality/CausalityModule.js';
import { bindPromptEditors } from '../components/PromptBinder.js';

// Import components
import { TabLayout } from '../components/TabLayout.js';
import { Toast } from '../components/Toast.js';
import { ModalService } from '../ui-kit/ModalService.jsx';
import { icon as devIcon } from '../ui/icons.js';
import * as ActionRegistry from '../core/ActionRegistry.js';
// Inline the product guide in the bundle for reliable PROD rendering
// Vite ?raw loads file contents as a string at build-time
import productGuideRaw from '../../PRODUCT_SPECIFICATION.md?raw';
import AnalyticsService from '../services/AnalyticsService.js';
import FeedbackService from '../services/FeedbackService.js';

/**
 * Main Application Class with Dependency Injection
 */
export class App {
  constructor() {
    // Optional DI container still supported but not required
    this.container = null;
    this.config = Config;
    this.appState = AppState;
    this.eventBus = EventBus;
    
    // Module orchestration
    this.moduleManager = new ModuleManager({
      services: container,
      events: EventBus,
      store: {
        dispatch: store.dispatch,
        getState: store.getState,
        subscribe: store.subscribe
      },
      config: Config
    });

    // Module and component maps (legacy access)
    this.modules = new Map();
    this.components = new Map();
    this.initialized = false;
    this.rootElement = null;
  }
  
  /**
   * Initialize the application
   */
  async initialize() {
    console.log('🚀 Initializing application UI...');
    
    // Initialize modules with dependencies
    await this.initializeModules();
    
    // Initialize components
    await this.initializeComponents();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Load saved data
    await this.loadSavedData();
    
    this.initialized = true;
    console.log('✅ Application UI initialized');
    // Start analytics session if enabled (safe no-op when disabled)
    try { AnalyticsService.startSession({ version: this.config?.APP_VERSION, env: this.config?.APP_ENV }); } catch (_) {}
  }
  
  /**
   * Initialize all modules
   */
  async initializeModules() {
    // Get modules from DI container if available
    if (this.container) {
      try {
        // Try to get modules from container
        const chatModule = await this.container.resolve('chatModule').catch(() => null);
        const portfolioModule = await this.container.resolve('portfolioModule').catch(() => null);
        const strategyModule = await this.container.resolve('strategyModule').catch(() => null);
        const initiativesModule = await this.container.resolve('initiativesModule').catch(() => null);
        const settingsModule = await this.container.resolve('settingsModule').catch(() => null);
        
        if (chatModule) { this.modules.set('chat', chatModule); this.moduleManager.register('chat', chatModule); }
        if (portfolioModule) { this.modules.set('portfolio', portfolioModule); this.moduleManager.register('portfolio', portfolioModule); }
        if (strategyModule) { this.modules.set('strategy', strategyModule); this.moduleManager.register('strategy', strategyModule); }
        if (initiativesModule) { this.modules.set('initiatives', initiativesModule); this.moduleManager.register('initiatives', initiativesModule); }
        if (settingsModule) { this.modules.set('settings', settingsModule); this.moduleManager.register('settings', settingsModule); }
        
        console.log('✅ Modules loaded from DI container');
      } catch (error) {
        console.warn('Could not load all modules from container:', error);
        await this.initializeModulesDirectly();
      }
    } else {
      // Fallback: Initialize modules directly (for backward compatibility)
      await this.initializeModulesDirectly();
    }
    
    // Initialize AI module separately (may not be in container yet)
    const aiModule = new AIModule();
    await aiModule.initialize(this.moduleManager.context);
    this.modules.set('ai', aiModule);
    this.moduleManager.register('ai', aiModule);
    
    console.log('✅ All modules loaded');
  }
  
  /**
   * Initialize modules directly (fallback)
   */
  async initializeModulesDirectly() {
    console.log('⚠️ Using fallback module initialization');
    
    const modules = [
      ['chat', new ChatModule()],
      ['portfolio', new PortfolioModule()],
      ['initiatives', new InitiativesModule()],
      ['settings', new SettingsModule()],
      ['strategy', new StrategyModule()],
      ['insights', new InsightsModule()],
      // Behind feature flag, switch to CausalityModule
      ['businessLevers', (Config?.FEATURES?.CAUSALITY === true) ? new CausalityModule() : new BusinessLeversModule()]
    ];
    
    for (const [name, module] of modules) {
      console.log(`  Loading ${name} module...`);
      await module.initialize(this.moduleManager.context);
      this.modules.set(name, module);
      this.moduleManager.register(name, module);
    }
  }
  
  /**
   * Initialize UI components
   */
  async initializeComponents() {
    // Create component instances
    const components = [
      ['tabLayout', new TabLayout()],
      ['toast', new Toast()]
    ];
    
    // Initialize each component
    for (const [name, component] of components) {
      await component.initialize();
      this.components.set(name, component);
    }
    
    console.log('✅ All components loaded');
  }
  
  /**
   * Set up global event listeners
   */
  setupEventListeners() {
    // Tab navigation
    EventBus.on(AppEvents.TAB_CHANGED, (tabId) => { this.handleTabChange(tabId); try { AnalyticsService.onTabChanged(tabId); } catch (_) {} });
    
    // State changes
    AppState.subscribe('currentTab', (tabId) => this.handleTabChange(tabId));
    
    // Initiative events
    EventBus.on(AppEvents.INITIATIVE_ADDED, () => {
      const portfolio = this.modules.get('portfolio');
      portfolio?.refresh?.();
    });
    
    // AI events
    EventBus.on(AppEvents.AI_REQUEST_START, () => AppState.setLoading(true));
    EventBus.on(AppEvents.AI_REQUEST_SUCCESS, () => AppState.setLoading(false));
    EventBus.on(AppEvents.AI_REQUEST_ERROR, (error) => {
      AppState.setLoading(false);
      this.showError(error);
    });
    
    // Toast events
    EventBus.on(AppEvents.TOAST_SHOW, ({ message, type }) => {
      this.components.get('toast')?.show(message, type);
    });
  }
  
  /**
   * Load saved data from storage
   */
  async loadSavedData() {
    try {
      // Use AppState which already loads from storage
      console.log('✅ Data loaded via AppState');
    } catch (error) {
      console.error('Failed to load saved data:', error);
    }
  }
  
  /**
   * Mount the application to DOM
   */
  async mount(rootElement) {
    this.rootElement = rootElement;
    
    // Create main layout
    const layout = this.createLayout();
    rootElement.innerHTML = layout;
    
    // Mount components
    const tabContainer = rootElement.querySelector('#tab-container');
    if (tabContainer) {
      const tabLayout = this.components.get('tabLayout');
      if (tabLayout) tabLayout.mount(tabContainer);
    }
    
    // Disable any diagnostic Safe UI Mode that may hide/disable modals
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('safeUIMode');
        // Ensure ActionRouter is enabled in production
        if (import.meta.env?.PROD) localStorage.removeItem('disableActionRouter');
      }
      if (typeof window !== 'undefined') {
        window.__SAFE_UI_MODE__ = false;
        if (import.meta.env?.PROD) window.__DISABLE_ACTION_ROUTER__ = false;
      }
      const safe = document.getElementById('safe-ui-mode-styles');
      if (safe) safe.remove();
    } catch (_) {}
    
    // Mount toast container
    if (!document.getElementById('toast-container')) {
      const toastContainer = document.createElement('div');
      toastContainer.id = 'toast-container';
      document.body.appendChild(toastContainer);
      const toast = this.components.get('toast');
      if (toast) toast.mount(toastContainer);
    }
    
    // ModalService ensures its own overlay root and styles via OverlayRoot

    // Route Help/Docs button via ActionRouter
    const mapping = {
      'open-help-docs': async () => {
        try {
          const esc = (s) => String(s)
            .replace(/&/g,'&amp;')
            .replace(/</g,'&lt;')
            .replace(/>/g,'&gt;');
          const safe = productGuideRaw && productGuideRaw.length > 0
            ? `<pre style="white-space:pre-wrap;max-height:70vh;overflow:auto;padding:12px;background:var(--bg-primary);border:1px solid var(--border-color);border-radius:8px">${esc(productGuideRaw)}</pre>`
            : `<iframe src="/PRODUCT_SPECIFICATION.md" style="width:100%;height:70vh;border:none"></iframe>`;
          ModalService.open({
            title: '📘 Product Guide',
            content: safe,
            actions: [{ text: 'Close', class: 'uk-btn uk-btn-secondary', action: () => {} }]
          });
        } catch (_) {
          ModalService.open({
            title: '📘 Product Guide',
            content: `<p>Open the product guide: <a href="/PRODUCT_SPECIFICATION.md" target="_blank" rel="noopener">PRODUCT_SPECIFICATION.md</a></p>`,
            actions: [{ text: 'Close', class: 'uk-btn uk-btn-secondary', action: () => {} }]
          });
        }
      },
      'open-feedback': async () => {
        // If Insights tab is enabled, navigate there instead of modal
        try {
          const useTab = (Config?.FEATURES?.INSIGHTS_TAB === true);
          if (useTab) {
            try { AppState.setState({ currentTab: 'insights' }); } catch(_) {}
            return;
          }
        } catch(_) {}
        try {
          const enabled = FeedbackService.init();
          const content = `
            <div style="display:flex;flex-direction:column;gap:12px">
              ${enabled ? '' : '<div style="color:var(--warning-color)">Note: Enable analytics in Settings to save feedback locally.</div>'}
              <label>Category
                <select id="fb-category" class="form-control">
                  <option value="ux">UX</option>
                  <option value="bug">Bug</option>
                  <option value="idea">Idea</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>Feedback
                <textarea id="fb-text" rows="4" class="form-control" placeholder="Write your feedback..."></textarea>
              </label>
              <label>Contact (optional)
                <input id="fb-contact" class="form-control" placeholder="Email or handle" />
              </label>
              <label style="display:flex;align-items:center;gap:8px">
                <input type="checkbox" id="fb-diag" /> Include diagnostics (version, browser)
              </label>
            </div>`;
          ModalService.open({
            title: '💬 Send Feedback',
            content,
            actions: [
              { text: 'Cancel', class: 'uk-btn uk-btn-secondary', action: () => {} },
              { text: 'Submit', class: 'uk-btn uk-btn-primary', action: async () => {
                const cat = document.getElementById('fb-category')?.value || 'ux';
                const text = document.getElementById('fb-text')?.value?.trim();
                const contact = document.getElementById('fb-contact')?.value?.trim() || '';
                const diag = !!document.getElementById('fb-diag')?.checked;
                if (text) await FeedbackService.submitAppFeedback({ category: cat, text, contact, includeDiagnostics: diag });
              }}
            ]
          });
        } catch (_) {}
      }
    };
    ActionRegistry.registerMap('app', mapping);
    
    // Initial tab
    this.handleTabChange('strategic-framework');
    try { AnalyticsService.onTabChanged('strategic-framework'); } catch (_) {}
    
    console.log('✅ Application mounted');

    // End analytics session on unload (best effort)
    try {
      window.addEventListener('beforeunload', () => { try { AnalyticsService.endSession(); } catch (_) {} });
    } catch (_) {}
  }
  
  /**
   * Create main application layout
   */
  createLayout() {
    const config = this.config || Config;
    const appName = config?.APP_NAME || 'Strategic Intelligence';
    
    return `
      <div class="app-container">
        <!-- Navigation -->
        <nav class="navbar">
          <div class="nav-container nav-inline" style="display:flex;align-items:center;justify-content:space-between;gap:12px">
            <div class="logo" style="display:flex;align-items:center;gap:8px">
              <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
                <rect width="40" height="40" rx="4" fill="#005EB8"/>
                <path d="M12 20L18 14L18 26L12 20Z" fill="white"/>
                <path d="M22 14H28V26H22V14Z" fill="white"/>
              </svg>
              <span>${appName}</span>
            </div>
            <div class="nav-hero" style="flex:1;display:flex;flex-direction:column">
              <span class="nav-combined">Shape the Future of Your Organization — AI-powered platform for strategic goal management</span>
            </div>
            <div class="nav-actions" style="display:flex;gap:10px">
              <button id="help-docs-btn" class="btn btn-primary btn-small" data-action="open-help-docs">Help / Docs</button>
              <button class="btn btn-primary btn-small" data-action="open-feedback">Give Feedback</button>
            </div>
          </div>
        </nav>

        <!-- Global Tab Row Host (sticky, full-width, consistent) -->
        <div id="tab-row"></div>
        
        <!-- Main Content -->
        <div class="container">
          <div id="tab-container"></div>
        </div>
      </div>
    `;
  }
  
  /**
   * Handle tab change
   */
  handleTabChange(tabId) {
    const blockTabs = ((typeof window !== 'undefined' && window.__BLOCK_TAB_CHANGES__ === true)
      || (typeof localStorage !== 'undefined' && localStorage.getItem('blockTabChanges') === 'true'))
      && (import.meta.env?.DEV);
    if (blockTabs) {
      console.warn('⛔ Ignoring tab change (DEV diagnostic mode):', tabId);
      return;
    }
    console.log(`📑 Switching to tab: ${tabId}`);
    
    // Load module for tab
    switch(tabId) {
      case 'strategic-framework':
        this.activateModule('strategy');
        break;
      case 'objectives':
        this.activateModule('chat');
        this.activateModule('initiatives');
        break;
      case 'portfolio':
        this.activateModule('portfolio');
        break;
      case 'insights':
        this.activateModule('insights');
        break;
      case 'business-levers':
        this.activateModule('businessLevers');
        break;
      case 'settings':
        this.activateModule('settings');
        break;
    }
    
    // Bind PromptEditor buttons unless BareClickMode is active
    const bare = (typeof window !== 'undefined' && window.__BARE_CLICK_MODE__ === true)
      || (typeof localStorage !== 'undefined' && localStorage.getItem('bareClickMode') === 'true');
    console.log('🧨 Minimal editor mode: PromptBinder bound to MinimalPromptEditor');

    // Register global QueryDatabase actions (idempotent)
    // QueryDatabase now handles its own UI events; no ActionRouter hooks required here
  }
  
  /**
   * Activate a module by name
   */
  activateModule(name) {
    const module = this.modules.get(name);
    if (module && typeof module.activate === 'function') {
      module.activate();
    }
  }
  
  /**
   * Show error message
   */
  showError(error) {
    const message = error.message || 'An error occurred';
    const toast = this.components.get('toast');
    if (toast) toast.show(message, 'error');
  }
  
  /**
   * Get module by name
   */
  getModule(name) {
    return this.modules.get(name);
  }
  
  /**
   * Get component by name
   */
  getComponent(name) {
    return this.components.get(name);
  }
}

// Export for use in main.js
export default App;

// In development, make available for debugging
if (import.meta.env?.DEV) {
  window.AppClass = App;
  console.log('🔧 App class available at window.AppClass for debugging');
}
